import React, { useState, useEffect } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonButton, IonText, IonSpinner, IonIcon, IonCheckbox, IonLabel,
} from '@ionic/react';
import { eyeOutline, eyeOffOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { authService } from '../services/authService';
import { API_BASE } from '../config/api';
import { useAuthStore } from '../store/authStore';
import { Game } from '../types';

const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 14, color: 'var(--ion-color-medium)' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', fontSize: 16, border: '1px solid var(--ion-color-medium)', borderRadius: 8, background: 'var(--ion-item-background, #fff)', color: 'var(--ion-text-color)' };

const GAME_ICONS: Record<string, string> = {
  pokemon: '\u26A1',
  yugioh: '\uD83D\uDC09',
  mtg: '\uD83E\uDDD9',
  sports: '\uD83C\uDFC8',
  custom: '\uD83E\uDDE9',
};

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [selectedGames, setSelectedGames] = useState<number[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const setAuth = useAuthStore((s) => s.setAuth);

  useEffect(() => {
    fetch(`${API_BASE}/api/games/presets`)
      .then((res) => res.json())
      .then((data) => setGames(data))
      .catch(() => { /* ignore - games are optional */ });
  }, []);

  const toggleGame = (gameId: number) => {
    setSelectedGames((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId]
    );
  };

  const handleRegister = async () => {
    setError('');
    if (!email || !username || !password) {
      setError('Please fill in all required fields');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await authService.register({ email, username, password, display_name: displayName || undefined });
      const { access_token } = await authService.login({ email, password });
      localStorage.setItem('token', access_token);
      if (selectedGames.length > 0) {
        await authService.updateProfile({ game_preferences: selectedGames });
      }
      const user = await authService.getMe();
      setAuth(user, access_token);
      history.push('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>Stashbinder - Register</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div style={{ maxWidth: 400, margin: '40px auto' }}>
          <h2>Create Account</h2>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Email *</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Username *</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Display Name</label>
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Password *</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...inputStyle, paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              >
                <IonIcon icon={showPassword ? eyeOffOutline : eyeOutline} style={{ fontSize: 20, color: 'var(--ion-color-medium)' }} />
              </button>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Confirm Password *</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{
                  ...inputStyle,
                  paddingRight: 44,
                  borderColor: confirmPassword && confirmPassword !== password ? 'var(--ion-color-danger)' : 'var(--ion-color-medium)',
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              >
                <IonIcon icon={showConfirmPassword ? eyeOffOutline : eyeOutline} style={{ fontSize: 20, color: 'var(--ion-color-medium)' }} />
              </button>
            </div>
            {confirmPassword && confirmPassword !== password && (
              <IonText color="danger"><small>Passwords do not match</small></IonText>
            )}
          </div>

          {games.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Game Preferences</label>
              <div style={{ border: '1px solid var(--ion-color-medium)', borderRadius: 8, padding: 8 }}>
                {games.map((game) => (
                  <div
                    key={game.id}
                    style={{ display: 'flex', alignItems: 'center', padding: '6px 4px', cursor: 'pointer' }}
                    onClick={() => toggleGame(game.id)}
                  >
                    <IonCheckbox checked={selectedGames.includes(game.id)} style={{ marginRight: 10 }} />
                    <IonLabel>{GAME_ICONS[game.slug] || ''} {game.name}</IonLabel>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <IonText color="danger"><p>{error}</p></IonText>}
          <IonButton expand="block" onClick={handleRegister} disabled={loading || (!!confirmPassword && confirmPassword !== password)} style={{ marginTop: 16 }}>
            {loading ? <IonSpinner name="crescent" /> : 'Register'}
          </IonButton>
          <IonButton expand="block" fill="clear" routerLink="/login">
            Already have an account? Login
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Register;
